import axios from "axios";
import { StatusCodes } from "http-status-codes";
import { config } from "../../../../config/envs";
import { isKillSwitchEnabled } from "../../../services/featureFlag";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlag";
import { getOrCreateDeviceId } from "../../../helpers/getDeviceId";

export const HYDRA_SILENT_ATTEMPT_KEY = "admin_hydra_silent_attempt";
export const HYDRA_FORCE_INTERACTIVE_KEY = "admin_hydra_force_interactive";

/** OAuth errors that mean silent SSO failed and interactive login is required. */
export const SILENT_SSO_FALLBACK_ERRORS = [
  "login_required",
  "interaction_required",
  "consent_required"
];

const AUTH_FRONT_START_PATH = "/oauth/start";

function authFrontUrl(path) {
  return `${config.authFront.url.replace(/\/$/, "")}${path}`;
}

function markSilentAttempt() {
  sessionStorage.setItem(HYDRA_SILENT_ATTEMPT_KEY, "1");
}

function clearSilentAttempt() {
  sessionStorage.removeItem(HYDRA_SILENT_ATTEMPT_KEY);
}

export function consumeSilentAttempt() {
  const wasSilent = sessionStorage.getItem(HYDRA_SILENT_ATTEMPT_KEY) === "1";
  clearSilentAttempt();
  return wasSilent;
}

export function markForceInteractive() {
  sessionStorage.setItem(HYDRA_FORCE_INTERACTIVE_KEY, "1");
}

export function clearForceInteractive() {
  sessionStorage.removeItem(HYDRA_FORCE_INTERACTIVE_KEY);
}

export function isForceInteractiveLogin() {
  if (typeof window === "undefined") {
    return false;
  }
  const fromQuery =
    new URLSearchParams(window.location.search).get("prompt") === "login";
  const fromStorage = sessionStorage.getItem(HYDRA_FORCE_INTERACTIVE_KEY) === "1";
  return fromQuery || fromStorage;
}

/**
 * Start login via auth-front (owns PKCE / Hydra authorize).
 * @param {{ prompt?: "none" | "login" }} [options]
 */
export function redirectToAuthFront(options = {}) {
  try {
    const params = new URLSearchParams({
      client_id: config.hydra.clientId,
      audience: config.hydra.audience,
      scope: config.hydra.scope
    });

    if (options.prompt === "none") {
      params.set("prompt", "none");
      markSilentAttempt();
    } else {
      clearSilentAttempt();
      if (options.prompt === "login") {
        params.set("prompt", "login");
      }
    }

    const startUrl = `${authFrontUrl(AUTH_FRONT_START_PATH)}?${params.toString()}`;

    new URL(startUrl);
    window.location.assign(startUrl);
    return true;
  } catch (err) {
    console.error("[auth] redirectToAuthFront: URL invalida", err);
    return false;
  }
}

const AUTH_SERVICE_TOKEN_REFRESH_PATH = "/api/v1/oauth/token/refresh";

function authServiceUrl(path) {
  return `${config.authService.baseUrl.replace(/\/$/, "")}${path}`;
}

const authServiceClient = axios.create({
  withCredentials: true,
  headers: { "X-Requested-With": "XMLHttpRequest" },
});

/**
 * Refresh (or, right after /callback, mint the first) access/id token via
 * the auth-service, authenticated by the HttpOnly refresh-token cookie.
 * clientId is required by RefreshTokenRequestBody (@NotBlank) to look up
 * which client policy/cookie name to read — refreshToken itself must
 * never be sent in the body: it's a fallback the auth-service only
 * consults for clients that cannot receive the cookie at all.
 */
export async function refreshViaAuthService() {
  const { data } = await authServiceClient.post(
    authServiceUrl(AUTH_SERVICE_TOKEN_REFRESH_PATH),
    { clientId: config.hydra.clientId }
  );
  return data;
}

const AUTH_SERVICE_LOGOUT_PATH = "/api/v1/oauth/logout";

/**
 * Logout is a top-level form POST (not fetch/XHR) so the SameSite=Lax/Strict
 * refresh-token cookie actually travels — a forged cross-site GET/fetch
 * can't ride along and trigger revocation.
 * client_id comes from this app's own Hydra config, not from the session:
 * the callback no longer receives a client_id to round-trip through Redux.
 */
export function submitLogoutToAuthService({ idToken }) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = authServiceUrl(AUTH_SERVICE_LOGOUT_PATH);
  form.style.display = "none";

  appendHiddenField(form, "client_id", config.hydra.clientId);
  appendHiddenField(form, "id_token_hint", idToken);

  document.body.appendChild(form);
  form.submit();
}

function appendHiddenField(form, name, value) {
  const field = document.createElement("input");
  field.type = "hidden";
  field.name = name;
  field.value = value;
  form.appendChild(field);
}

export const LOGIN_URL = process.env.REACT_APP_API_URL + "/auth/admin/login";
export const VERIFY_2FA_URL = process.env.REACT_APP_API_URL + "/auth/2fa/verify";
export const RESEND_2FA_URL =
  process.env.REACT_APP_API_URL + "/auth/2fa/resend-code";
export const REGISTER_URL = config.app.adminBaseUrl + "/auth/register";
export const REQUEST_PASSWORD_URL =
  process.env.REACT_APP_API_URL + "/auth/forgot-password";

export const ME_URL = process.env.REACT_APP_API_URL + "/me";
export const ME_V2_URL = process.env.REACT_APP_API_URL + "/v2/me";

export function login(email, password) {
  return axios.post(
    LOGIN_URL,
    { email, password, admin: true },
    { headers: { "x-device-id": getOrCreateDeviceId() } }
  );
}

export function verifyTwoFactor(code, tempToken) {
  return axios.post(
    VERIFY_2FA_URL,
    { code },
    {
      headers: {
        Authorization: `Bearer ${tempToken}`,
        "x-device-id": getOrCreateDeviceId()
      },
      skipAuthRefresh: true
    }
  );
}

export function resendTwoFactorCode(tempToken) {
  return axios.post(
    RESEND_2FA_URL,
    {},
    {
      headers: {
        Authorization: `Bearer ${tempToken}`,
        "x-device-id": getOrCreateDeviceId()
      },
      skipAuthRefresh: true
    }
  );
}

export function register(email, fullname, username, password) {
  return axios.post(REGISTER_URL, { email, fullname, username, password });
}

export function requestPassword(email) {
  return axios.post(REQUEST_PASSWORD_URL, { email });
}

export async function getUserByToken() {
  // Authorization head should be fulfilled in interceptor.
  const useV2 = await isKillSwitchEnabled(
    FEATURE_FLAGS.KILL_SWITCH.DISABLE_CONSUME_DEPRECATED_ME_ENDPOINT
  );

  try {
    return await axios.get(useV2 ? ME_V2_URL : ME_URL);
  } catch (error) {
    if (!useV2 && error?.response?.status === StatusCodes.GONE) {
      return axios.get(ME_V2_URL);
    }

    throw error;
  }
}
